/**
 * Compact, minute-granular duration: `0m`, `42m`, `1h 5m`, `2d 3h`. Used for
 * "connected for" style labels that refresh on a coarse timer.
 */
export function formatElapsed(ms: number): string {
  const totalMin = Math.floor(Math.max(0, ms) / 60_000);
  if (totalMin < 60) return `${totalMin}m`;
  const hours = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  if (hours < 24) return min === 0 ? `${hours}h` : `${hours}h ${min}m`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours === 0 ? `${days}d` : `${days}d ${remHours}h`;
}

/** Second-granular overrun timer: `+05:12`, `+1:05:12`. */
export function formatOverrun(ms: number): string {
  const totalSec = Math.floor(Math.max(0, ms) / 1000);
  const hours = Math.floor(totalSec / 3600);
  const min = Math.floor((totalSec % 3600) / 60);
  const sec = totalSec % 60;
  const mmss = `${pad(min)}:${pad(sec)}`;
  return hours > 0 ? `+${hours}:${mmss}` : `+${mmss}`;
}

/**
 * Time left until an end: whole minutes, rounded up so "1m" is shown until
 * the very end; seconds only in the final minute.
 */
export function formatRemaining(ms: number): string {
  const clamped = Math.max(0, ms);
  if (clamped < 60_000) return `${Math.ceil(clamped / 1000)}s`;
  return formatElapsed(Math.ceil(clamped / 60_000) * 60_000);
}

/**
 * Second-granular countdown to a deadline: `09:59`, `00:01`, `00:00`. Seconds
 * are rounded up, so `00:00` only shows once the deadline has passed.
 */
export function formatCountdown(ms: number): string {
  const totalSec = Math.ceil(Math.max(0, ms) / 1000);
  return `${pad(Math.floor(totalSec / 60))}:${pad(totalSec % 60)}`;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}
